import Image from 'next/image';
import { useContext, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import ClientContext from '@/contexts/client.context';

import { Container, Heading } from '@swordhealth/ui-corporate';
import { getImgData } from '@/utils/images';
import { formattedTitle } from '@/utils/formatted-title';
import useBetterMediaQuery from '@/utils/useBetterMediaQuery';
import theme from '@/utils/styles-variables';
import Portal from '@/utils/create-portal';

import EnrollButton from '@/app/components/core/EnrollButton';
import { LogosBar } from '../../shared/LogosBar/LogosBar';
import { Logos } from '../../shared/Logos/Logos';

import {
  ImageWrapper,
  ButtonWrapper,
  Content,
  Subtitle,
  ExtraLabel,
  ContentWrapper,
  LogosWrapper,
  BackgroundShape,
  FixedWrapper,
  EnrollButtonWrapper,
  EnrollContainer,
} from './styles';

const HeroJulyThematic = ({ content, userName }) => {
  const { client } = useContext(ClientContext);
  const { title, subtitle, buttonLabel, label, media } = content;
  const isMobile = useBetterMediaQuery(`(max-width: ${theme.breakpoints.max_sm}px)`);

  const [ref, inView, entry] = useInView({
    threshold: 1,
    initialInView: true,
    rootMargin: '75px',
  });

  const [ref1, inView1, entry1] = useInView({
    threshold: 1,
    initialInView: true,
    rootMargin: '-10px',
  });

  const shouldShowFixedBar = !inView && !isMobile && entry?.boundingClientRect?.top < 100;
  const shouldShowFixedButton = !inView1 && isMobile && entry1?.boundingClientRect?.top < 100;

  const enrollButton = (isFixed = false) => (
    <EnrollButton
      id={isFixed ? 'fixed_header_button' : 'header_button'}
      className="button"
      isActive={client?.isActive}
      client={client}
      buttonLabel={buttonLabel}
      product={client?.product}
    />
  );

  const logos = useMemo(() => {
    const logoData = client.logo?.data || client.logos?.data;
    return logoData ? (Array.isArray(logoData) ? logoData.slice(0, 3) : [logoData]) : [];
  }, [client]);

  return (
    <>
      <Container xAlign="center">
        {shouldShowFixedBar && (
          <LogosBar
            logos={!client?.clientKey ? [] : logos}
            centered
            showSwordLogo={!client?.clientKey ? true : client?.showSwordLogo}
            client={client}
            withBackground
            showButton={shouldShowFixedBar}
            buttonLabel={buttonLabel}
          />
        )}
        <Content>
          <BackgroundShape />
          <LogosWrapper ref={ref}>
            <div>
              {client.clientKey ? (
                <Logos
                  logos={logos}
                  centered
                  showSwordLogo={client.showSwordLogo}
                  client={client}
                  withBackground
                  delay={false}
                />
              ) : (
                <Logos
                  logos={[]}
                  centered
                  showSwordLogo
                  client={client}
                  withBackground
                  delay={false}
                />
              )}
            </div>
          </LogosWrapper>

          <ContentWrapper>
            <Heading as="h1" size="3xl" weight="bold">
              {formattedTitle({ title, userName })}
            </Heading>

            {subtitle && <Subtitle className="notGradient">{subtitle}</Subtitle>}

            <ButtonWrapper ref={ref1}>
              {enrollButton(false)}
              {label && <ExtraLabel>{label}</ExtraLabel>}
            </ButtonWrapper>
          </ContentWrapper>
          {media.data.map((image, index) => {
            const dataImg = getImgData(image);
            return (
              <ImageWrapper $position={index} key={`hero-image-wrapper-${index}`}>
                <Image src={dataImg.url} alt={dataImg.alternativeText} fill priority />
              </ImageWrapper>
            );
          })}

          {shouldShowFixedButton && (
            <Portal portalId="sticky-enroll-button-portal">
              <FixedWrapper $visible={shouldShowFixedButton}>
                <EnrollButtonWrapper>
                  <EnrollContainer>{enrollButton(true)}</EnrollContainer>
                </EnrollButtonWrapper>
              </FixedWrapper>
            </Portal>
          )}
        </Content>
      </Container>
    </>
  );
};

HeroJulyThematic.defaultConfig = {
  colored: true,
  marginBottom: false,
  rounded: true,
  spaceBottom: true,
  spaceTop: true,
  withGradient: false,
  theme: 'yellow',
};

HeroJulyThematic.lockedConfig = {
  rounded: false,
  spaceTop: true,
};

export default HeroJulyThematic;
